import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './styles/global.css';
import Template from "./template/Template";
import ProductList from "./products/ProductList";
import Login from './components/Login';
import SignUp from './components/SignUp';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ActivateAccount from './components/ActivateAccount';
import AuthComplete from './components/AuthComplete';
import ConfirmEmail from './components/ConfirmEmail';
import EmailVerificationHandler from './components/EmailVerificationHandler';
import ForceChangePassword from './components/ForceChangePassword';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { AuthProvider } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import PrivateRoute from './components/PrivateRoute';
import Package from './subscription/Package';
import Payment from './subscription/Payment';
import Complete from './subscription/Complete';
import NotFound from './components/NotFound';
import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/dashboard/Dashboard';
import ProductManagement from './admin/products/ProductManagement';
import SubscriptionManagement from './admin/subscriptions/SubscriptionManagement';
import OrderManagement from './admin/orders/OrderManagement';
import UserManagement from './admin/users/UserManagement';
import SettingsManagement from './admin/settings/SettingsManagement';
import EmailTemplateSettings from './admin/settings/EmailTemplateSettings';
import { CompanyProvider } from './context/CompanyContext';
import UserDashboard from './dashboard/UserDashboard';
import UserSubscription from './subscription/UserSubscription';
import Response from './subscription/Response';
import Orders from './subscription/Orders';
import CarrierManagement from './admin/carriers/CarrierManagement';
import DiscountCodeManagement from './admin/discount-codes/DiscountCodeManagement';
import ErrorBoundary from './components/ErrorBoundary';
import CompanyManagement from './admin/companies/CompanyManagement';
import CompanyStats from './admin/companies/CompanyStats';
import AdminNotificationsPage from './admin/notifications/NotificationsPage';
import AuditLogsPage from './admin/audit-logs/AuditLogsPage';
import UserNotificationsPage from './notifications/NotificationsPage';


function App() {
  return (
    <ErrorBoundary showDetails={process.env.NODE_ENV === 'development'}>
      <BrandingProvider>
        <AuthProvider>
          <SubscriptionProvider>
          <Toaster 
            position="bottom-right"
            toastOptions={{
              duration: 3000,
              style: {
                borderRadius: '10px',
                background: '#333',
                color: '#fff',
              },
            }}
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/activate-account" element={<ActivateAccount />} />
            <Route path="/auth/complete" element={<AuthComplete />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/verify-email" element={<EmailVerificationHandler />} />
            <Route path="/force-change-password" element={<ForceChangePassword />} />

            {/* Product Routes - Public */}
            <Route path="/" element={<Navigate to="/products" replace />} />
            <Route path="products" element={<Template><ProductList /></Template>} />

            {/* Subscription Routes - Public with auto-registration */}
            <Route path="subscription">
              <Route index element={<Template><NotFound /></Template>} />
              <Route path="package" element={<Template><Package /></Template>} />
              <Route path="payment" element={<Template><Payment /></Template>} />
              <Route path="complete" element={<Template><Complete /></Template>} />
              <Route path="user/:id" element={
                <PrivateRoute>
                  <Template><UserSubscription /></Template>
                </PrivateRoute>
              } />
              <Route path="response" element={
                <PrivateRoute>
                  <Template><Response /></Template>
                </PrivateRoute>
              } />
              <Route path="*" element={<Template><NotFound /></Template>} />
            </Route>

            {/* Admin Routes */}
            <Route
              path="admin"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <Dashboard />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            {/* Admin Child Routes need a separate wrapper if we want them to render inside AdminLayout unless AdminLayout uses <Outlet> */}
            {/* Checking AdminLayout logic... Usually we wrap all admin routes in one Route with sub-routes */}
            {/* Since App.js seems to define /admin layout separately, I need to check how nested routes are handled. */}
            {/* Looking at lines 90-100, /admin renders Dashboard inside AdminLayout. This suggests other admin pages might need their own routes or nested routes. */}
            {/* Assuming there's a Route for /admin/* or individual routes. Let me check the file content closer. */}
            {/* Wait, the view_file showed lines 1-100. I need to see how other admin pages are mounted. */}
            {/* I'll add them as sibling routes for now, matching the pattern if visible or assuming standard react-router nesting */}
            {/* Actually, I see imports but not the <Route> definitions for other admin pages in the snippet. I should fetch more lines of App.js first to be safe. */}

            <Route
              path="admin/companies"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <CompanyManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/companies/:id/stats"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <CompanyStats />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/products"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <ProductManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/subscriptions"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <SubscriptionManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/orders"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN', 'SHIPPING_STAFF']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <OrderManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <UserManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/carriers"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN', 'SHIPPING_STAFF']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <CarrierManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/discount-codes"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <DiscountCodeManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/settings"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <SettingsManagement />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/email-branding"
              element={
                <PrivateRoute allowedRoles={['COMPANY_ADMIN', 'SUPER_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <EmailTemplateSettings />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/audit-logs"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <AuditLogsPage />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/notifications"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN', 'SHIPPING_STAFF']}>
                  <CompanyProvider>
                    <AdminLayout>
                      <AdminNotificationsPage />
                    </AdminLayout>
                  </CompanyProvider>
                </PrivateRoute>
              }
            />
            <Route
              path="admin/profile"
              element={
                <PrivateRoute allowedRoles={['SUPER_ADMIN', 'COMPANY_ADMIN', 'SHIPPING_STAFF']}>
                  <Template>
                    <UserDashboard />
                  </Template>
                </PrivateRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="dashboard/*"
              element={
                <PrivateRoute>
                  <Template><UserDashboard /></Template>
                </PrivateRoute>
              }
            />

            {/* User Notifications */}
            <Route
              path="notifications"
              element={
                <PrivateRoute>
                  <Template><UserNotificationsPage /></Template>
                </PrivateRoute>
              }
            />

            {/* Subscription Routes */}
            <Route
              path="subscription/response"
              element={
                <PrivateRoute>
                  <Template><Response /></Template>
                </PrivateRoute>
              }
            />

            {/* Müşteri Hesap Sayfası - Artık gerekli değil çünkü dashboard'a taşındı */}
            {/* <Route path="/account" element={
              <PrivateRoute>
                <UserDashboard />
              </PrivateRoute>
            } /> */}

            <Route path="/orders" element={
              <PrivateRoute>
                <Template><Orders /></Template>
              </PrivateRoute>
            } />

            <Route path="*" element={<Template><NotFound /></Template>} />
          </Routes>
        </SubscriptionProvider>
        </AuthProvider>
      </BrandingProvider>
    </ErrorBoundary>
  );
}

export default App;
